// Lasting addresses for Draksy photos on a public site: /media/<uuid> (the original) and /media/<uuid>/<400|1200|2000>.webp
// (a sized copy), on the site's own domain. They carry no token and no file name, so a link preview (LinkedIn, Slack) or a
// search engine can keep them for good. The /media route (src/server/api/media.js) signs a fresh bristol address per request.
// The studio preview (naples-preview) shows unpublished photos and keeps signed addresses (src/server/attachmentUrl.js).

export const MEDIA_SIZES = Object.freeze([400, 1200, 2000]);

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const ATTACHMENT_PATH = new RegExp(`^/api/models/attachment/(${UUID})$`);
const MEDIA_PATH = new RegExp(`^/media/(${UUID})(?:/(?:(\\d{3,4})\\.webp|(share)\\.jpg))?$`);
const MEDIA_URL = new RegExp(`^(?:https?://[^/?#]+)?/media/(${UUID})$`);
const MEDIA_ANYWHERE = new RegExp(`/media/(${UUID})`, 'g');

// The id in a bare "/api/models/attachment/<uuid>" path, else "".
export const attachmentIdOf = (path) => {
    const m = typeof path === 'string' && path.match(ATTACHMENT_PATH);
    return m ? m[1].toLowerCase() : '';
};

export const mediaUrl = (id, size) => (size ? `/media/${id}/${size}.webp` : `/media/${id}`);

// The link-preview copy: a JPEG at most 1200 wide (src/server/api/media.js makes it from the 1200 copy). LinkedIn and
// other link previews leave out a WebP og:image, so share tags point here, never at a .webp.
export const shareMediaUrl = (id) => `/media/${id}/share.jpg`;

// {id, size} from a request path ("/media/<uuid>" gives size 0), or null when it is not a valid /media path.
export function parseMediaPath(pathname) {
    const m = typeof pathname === 'string' && pathname.match(MEDIA_PATH);
    if (!m) return null;
    if (m[3]) return {id: m[1].toLowerCase(), size: 1200, share: true};
    const size = m[2] ? Number(m[2]) : 0;
    if (m[2] && !MEDIA_SIZES.includes(size)) return null;
    return {id: m[1].toLowerCase(), size};
}

// The id of a plain /media/<uuid> address (relative or absolute), else "".
export const mediaIdOf = (url) => {
    const m = typeof url === 'string' && url.match(MEDIA_URL);
    return m ? m[1].toLowerCase() : '';
};

// Every photo id a piece of text points at through /media (sized or not).
export const mediaIdsIn = (text) => [...new Set([...String(text ?? '').matchAll(MEDIA_ANYWHERE)].map((m) => m[1].toLowerCase()))];

// A /media address on the given site (for og:image and other places a crawler needs a full address). Anything else is left alone.
export function absoluteUrl(url, origin) {
    if (typeof url !== 'string' || !url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('//')) return `https:${url}`;
    if (url.startsWith('/')) return `${origin}${url}`;
    return url;
}

// s-maxage: a shared cache in front of the site (the photo cache on the Caddy box) keeps a copy for a week at most, so a
// photo taken off a site stops being served from there within 7 days; browsers still keep theirs for a year.
export const IMMUTABLE = 'public, max-age=31536000, s-maxage=604800, immutable';
const SHORT = 'public, max-age=3600';

// Cache-Control for a /media response. A photo's id never changes what it shows, so the original at /media/<uuid> and a real
// WebP copy at /media/<uuid>/<size>.webp last a year. bristol answers a sized request with the ORIGINAL when it has no copy
// (older photos, formats it cannot resize): that must not be pinned for a year under a ".webp" address, so it gets an hour.
export function mediaCacheControl({size, contentType}) {
    if (!size) return IMMUTABLE;
    return String(contentType || '').split(';')[0].trim().toLowerCase() === 'image/webp' ? IMMUTABLE : SHORT;
}

// What a browser may be told a body is. Anything that is not a picture, video or PDF is sent as a download, never rendered
// on the site's own domain (a stored .html or .svg would otherwise run as a page of that site).
const SAFE_TYPE = /^(image\/(?!svg)[a-z0-9.+-]+|video\/[a-z0-9.+-]+|application\/pdf)$/;
export function mediaContentType(upstream) {
    const type = String(upstream || '').split(';')[0].trim().toLowerCase();
    return SAFE_TYPE.test(type) ? {type, safe: true} : {type: 'application/octet-stream', safe: false};
}
